import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useServerFilters } from '@/hooks/use-server-filters';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PageProps, type PaginationMeta } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { route } from 'ziggy-js';

interface RequestListItem {
    id: string;
    public_id: string | null;
    title: string;
    status: string;
    priority: string;
    template_name: string | null;
    due_date: string | null;
    created_at: string | null;
}

interface IndexPageProps extends PageProps {
    requests: RequestListItem[];
    pagination: PaginationMeta;
    filters: { status?: string; page?: number; per_page?: number };
    statuses: string[];
    [key: string]: unknown;
}

const statusBadgeClassMap: Record<string, string> = {
    open: 'bg-info-subtle text-info-subtle-foreground',
    in_progress: 'bg-cat-purple text-cat-purple-foreground border-cat-purple-foreground/30',
    on_hold: 'bg-warning-subtle text-warning-subtle-foreground',
    resolved: 'bg-success-subtle text-success-subtle-foreground',
    closed: 'bg-muted text-muted-foreground',
    rejected: 'bg-danger-subtle text-danger-subtle-foreground',
};

const priorityBadgeClassMap: Record<string, string> = {
    low: 'bg-muted text-muted-foreground',
    normal: 'bg-info-subtle text-info-subtle-foreground',
    high: 'bg-warning-subtle text-warning-subtle-foreground',
    urgent: 'bg-danger-subtle text-danger-subtle-foreground',
};

export default function ServiceRequestsIndexPage() {
    const { props } = usePage<IndexPageProps>();
    const { requests, pagination, filters, statuses } = props;
    const { t } = useTranslations();
    const { applyFilters, loading } = useServerFilters(filters, '/service-requests');

    const breadcrumbs = [{ title: t('service_requests.title', 'Service Requests'), href: '/service-requests' }];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('service_requests.my_requests_title', 'My Requests')} />

            <PageHeader
                title={t('service_requests.my_requests_title', 'My Requests')}
                subtitle={t('service_requests.my_requests_subtitle', 'Track the status of your submitted service requests.')}
                actions={
                    <Button asChild>
                        <Link href={route('service-requests.catalog')}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('service_requests.new_request', 'New Request')}
                        </Link>
                    </Button>
                }
            />

            <PageContent className="space-y-5">
                <div className="bg-card rounded-xl border p-4">
                    <Select value={filters.status || 'all'} onValueChange={(v) => applyFilters({ status: v === 'all' ? undefined : v, page: 1 })}>
                        <SelectTrigger className="w-56">
                            <SelectValue placeholder={t('service_requests.filter_all_statuses', 'All statuses')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('service_requests.filter_all_statuses', 'All statuses')}</SelectItem>
                            {statuses.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="bg-card overflow-hidden rounded-xl">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>{t('service_requests.template', 'Template')}</TableHead>
                                <TableHead>{t('service_requests.status', 'Status')}</TableHead>
                                <TableHead>{t('service_requests.priority', 'Priority')}</TableHead>
                                <TableHead>{t('service_requests.due_date', 'Due Date')}</TableHead>
                                <TableHead>{t('service_requests.created_at', 'Submitted')}</TableHead>
                                <TableHead className="text-right"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-muted-foreground py-12 text-center">
                                        {t('service_requests.no_requests', 'No requests found.')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map((req) => (
                                    <TableRow key={req.id}>
                                        <TableCell className="font-semibold">{req.public_id ?? req.id.slice(0, 8)}</TableCell>
                                        <TableCell>{req.template_name ?? req.title}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={statusBadgeClassMap[req.status] ?? ''}>
                                                {req.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={priorityBadgeClassMap[req.priority] ?? ''}>
                                                {req.priority}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-sm">{req.due_date ?? '—'}</TableCell>
                                        <TableCell className="text-muted-foreground text-sm">
                                            {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button asChild variant="outline" size="sm">
                                                <Link href={route('service-requests.show', req.id)}>{t('service_requests.view', 'View')}</Link>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                <PaginationControls
                    pagination={pagination}
                    loading={loading}
                    onPageChange={(page) => applyFilters({ page })}
                    onPerPageChange={(perPage) => applyFilters({ per_page: perPage, page: 1 })}
                />
            </PageContent>
        </AppLayout>
    );
}
